import type { CartGetItem } from 'src/types';

type Args = {
  cartStore: string;
  cartItems: CartGetItem[];
  cartReferrer?: string;
  tagParam: string | null;
};

export const sendCartToAmazonLocal = ({
  cartStore,
  cartItems,
  cartReferrer,
  tagParam,
}: Args) => {
  const afxLandingPayload = {
    ingredients: [] as any,
    saved: false,
    recipeComposition: {
      saved: false,
    },
  };

  for (let i = 0; i < cartItems.length; i++) {
    /*
ingridients value:
{
  "ingredients": [
    {
      "name": "cartItems[i].title",
      "asinOverrides": ["cartItems[i].asin"],
      "componentIndex": i,
      "quantityList": [
        {
          "unit": "COUNT",
          "amount": cartItems[i].quantity
        }
      ],
      "exclusiveOverride": false
    }
  ],
  "saved": false,
  "recipeComposition": {
    "saved": false
  }
}

Unrecognized unit for ingredient: green apples. Valid units are: COUNT, CLOVES, SLICES, STALKS, LEAVES, BUNCHES, KILOGRAMS, GRAMS, POUNDS, OUNCES, PINCHES, LITERS, CENTILITERS, MILLILITERS, CC, DROPS, GALLONS, QUARTS, PINTS, CUPS, FL_OZ, HEAPING_TBSP, TBSP, HEAPING_TSP, TSP, JP_PIECES, JP_SHAKES, JP_PACKS, JP_BOTTLES, JP_CANS, JP_BUNDLES, JP_BUNCHES, JP_LOAVES, JP_SHOSHO, JP_CUPS, JP_TSUKAMI, JP_NIGIRI, JP_TEKIRYO, JP_BIG_RICE_BOWLS, JP_RICE_BOWLS, JP_TSUBU, JP_TSUTSUMI, JP_CUT_PIECES, JP_MAI, JP_GO, JP_CHO, JP_BI, JP_HAI, JP_KABU, JP_FUSHI, JP_TAMA, JP_HARA, JP_SAKU, JP_KUKI, JP_HIKI, DE_PACK, DE_CAN, DE_HANDFUL, DE_BRANCHES, DE_DASH
  */

    let uom = 'COUNT';
    let uomAmount: number | string | undefined = cartItems[i].quantity;

    if (cartItems[i].uom && cartItems[i].uom?.includes('lb')) {
      uom = 'POUNDS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('oz')) {
      uom = 'OUNCES';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('kg')) {
      uom = 'KILOGRAMS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('g')) {
      uom = 'GRAMS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('ml')) {
      uom = 'MILLILITERS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('pt')) {
      uom = 'PINTS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('qt')) {
      uom = 'QUARTS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    } else if (cartItems[i].uom && cartItems[i].uom?.includes('gal')) {
      uom = 'GALLONS';
      uomAmount = cartItems[i].uom?.split(' ')[0];
    }

    afxLandingPayload.ingredients.push({
      name: `&nbsp;of ${cartItems[i].title}`,
      asinOverrides: [cartItems[i].asin],
      componentIndex: i,
      quantityList: [
        {
          // unit: 'COUNT',
          // amount: cartItems[i].quantity,
          unit: uom,
          amount: uomAmount,
        },
      ],
      exclusiveOverride: false,
    });
  }

  // Every item in a local-market / Fresh cart carries the same store id
  // (almBrandId), but out-of-stock / manually-added items omit it. Use the
  // first item that has one; the old code used the last item's brandId, which
  // is undefined whenever the cart ends on such an item, and Amazon rejects the
  // POST with a 500 on almBrandId=undefined. Fall back to the store's default
  // brand id when no item carries one.
  const defaultAlmBrandId = cartStore.endsWith('/cart/fresh')
    ? 'QW1hem9uIEZyZXNo' // Amazon Fresh
    : 'VUZHIFdob2xlIEZvb2Rz'; // UFG Whole Foods (local market)
  const almBrandId = cartItems.find((item) => item.brandId)?.brandId ?? defaultAlmBrandId;

  let amzAffilTag = '';

  const host = new URL(cartStore).host;

  // use host to get the affiliate tag
  if (host.includes('amazon.com')) {
    amzAffilTag = 'sacsite-20';
  } else if (host.includes('amazon.co.uk')) {
    amzAffilTag = 'sacsite-21';
  } else if (host.includes('amazon.ca')) {
    amzAffilTag = 'sacsite05-20';
  } else if (host.includes('amazon.com.au')) {
    amzAffilTag = 'sacsite0e-22';
  } else if (host.includes('amazon.com.be')) {
    amzAffilTag = 'sacsite08-21';
  } else if (host.includes('amazon.co.jp')) {
    amzAffilTag = 'sacsite-22';
  } else if (host.includes('amazon.de')) {
    amzAffilTag = 'sacsite02-21';
  } else if (host.includes('amazon.es')) {
    amzAffilTag = 'sacsite0d-21';
  } else if (host.includes('amazon.fr')) {
    amzAffilTag = 'sacsite03-21';
  } else if (host.includes('amazon.it')) {
    amzAffilTag = 'sacsite0e00-21';
  } else if (host.includes('amazon.nl')) {
    amzAffilTag = 'sacsite0a2-21';
  } else if (host.includes('amazon.pl')) {
    amzAffilTag = 'sacsite06-21';
  } else if (host.includes('amazon.se')) {
    amzAffilTag = 'sacsite0fe-21';
  } else if (host.includes('amazon.sg')) {
    amzAffilTag = 'sacsite0b-22';
  } else {
    amzAffilTag = 'sacsite-20';
  }

  if (cartReferrer) {
    amzAffilTag = cartReferrer;
  }

  if (tagParam) {
    amzAffilTag = tagParam;
  }

  const afxForm = `<form id="alm-form" method="POST" action="https://${host}/afx/ingredients/landing?tag=${amzAffilTag}&AssociateTag=${amzAffilTag}">
      <input type="hidden" id="ingredients" name="ingredients" value="${JSON.stringify(afxLandingPayload).replaceAll('"', '&quot;')}">
      <input type="hidden" id="almBrandId" name="almBrandId" value="${almBrandId}">
      <input type="hidden" id="associateTag" name="associateTag" value="${amzAffilTag}">
      <input type="hidden" id="tag" name="tag" value="${amzAffilTag}">
      <input type="submit" value="Continue">
    </form>`;

  // add afxForm into the DOM
  const form = document.createElement('span');
  form.innerHTML = afxForm;
  document.body.appendChild(form);

  (document.getElementById('alm-form') as any).submit();
};
